import React, { useEffect, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import CrudTableCard from 'modules/common/crudTableCard.component';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { showToast } from 'modules/layout/layout.actions';
import {
  DELETE_AUTH_PT_CODE_FAILURE,
  POST_AUTH_PT_CODE_FAILURE,
  UPDATE_AUTH_PT_CODE_FAILURE
} from '../store/types/authorizationTypes';
import { deleteAuthPtCode, postAuthPtCode, updateAuthPtCode } from '../store/actions/authorizations.actions';

const getUpdatedPtCodeRows = response => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.ptCodeHistories)) return response.ptCodeHistories;
  return [];
};

const sortPtCodesByEffectiveEndDate = ptCodes => {
  if (!Array.isArray(ptCodes)) return [];

  return [...ptCodes].sort((a, b) => {
    const aEndDate = a?.effectiveEndDate;
    const bEndDate = b?.effectiveEndDate;

    const aIsNull = aEndDate === null || aEndDate === undefined || aEndDate === '';
    const bIsNull = bEndDate === null || bEndDate === undefined || bEndDate === '';

    if (aIsNull && !bIsNull) return -1;
    if (!aIsNull && bIsNull) return 1;
    if (aIsNull && bIsNull) return 0;

    const aTime = new Date(aEndDate).getTime();
    const bTime = new Date(bEndDate).getTime();

    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;

    return bTime - aTime;
  });
};

const PtCodesCard = props => {
  const { ptCodeHistories, fileId, clientRecordId, hideEdit } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState(sortPtCodesByEffectiveEndDate(ptCodeHistories || []));

  useEffect(() => {
    setRows(sortPtCodesByEffectiveEndDate(ptCodeHistories || []));
  }, [ptCodeHistories]);

  const tabs = [{ header: 'PT Codes', order: 1, iconPath: ['fas', 'fa-money-check-alt'] }];

  const columns = [
    { accessorKey: 'projectCode', header: 'Project Code', type: 'text', required: true, size: 100 },
    { accessorKey: 'taskCode', header: 'Task Code', type: 'text', required: true, size: 100 },
    { accessorKey: 'percentage', header: 'Percentage', type: 'number', required: false, size: 70, hideInTable: false  },
    {
      accessorKey: 'effectiveStartDate',
      header: 'Start Date',
      type: 'date',
      required: true,
      size: 80,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '')
    },
    {
      accessorKey: 'effectiveEndDate',
      header: 'End Date',
      type: 'date',
      required: false,
      size: 80,
      hideInTable: false,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '')
    },
    { accessorKey: 'notes', header: 'Notes', type: 'notes', required: false, size: 150, hideInTable: false }
  ];

  const mapPayload = values => ({
    authorizationFileId: Number(fileId),
    clientRecordId: Number(clientRecordId),
    projectCode: values.projectCode || null,
    taskCode: values.taskCode || null,
    percentage: values.percentage === '' || values.percentage === null || values.percentage === undefined ? null : Number(values.percentage),
    effectiveStartDate: values.effectiveStartDate || null,
    effectiveEndDate: values.effectiveEndDate || null,
    notes: values.notes || null
  });

  const createRow = async values => {
    setIsLoading(true);
    const resp = await props.postAuthPtCode(fileId, mapPayload(values));

    if (resp.type === POST_AUTH_PT_CODE_FAILURE) {
      props.showToast('Failed to create PT code, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    }

    setRows(sortPtCodesByEffectiveEndDate(getUpdatedPtCodeRows(resp.response)));
    setIsLoading(false);
    return true;
  };

  const updateRow = async (row, values) => {
    setIsLoading(true);
    const ptCodeHistoryId = row?.original?.ptCodeHistoryId;
    const request = {
      ...mapPayload(values),
      ptCodeHistoryId
    };

    const resp = await props.updateAuthPtCode(fileId, ptCodeHistoryId, request);
    if (resp.type === UPDATE_AUTH_PT_CODE_FAILURE) {
      props.showToast('Failed to update PT code, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    }

    setRows(sortPtCodesByEffectiveEndDate(getUpdatedPtCodeRows(resp.response)));
    setIsLoading(false);
    return true;
  };

  const deleteRow = async row => {
    setIsLoading(true);
    const ptCodeHistoryId = row?.original?.ptCodeHistoryId;

    const resp = await props.deleteAuthPtCode(fileId, ptCodeHistoryId);
    if (resp.type === DELETE_AUTH_PT_CODE_FAILURE) {
      props.showToast('Failed to delete PT code, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    }

    setRows(sortPtCodesByEffectiveEndDate(getUpdatedPtCodeRows(resp.response)));
    setIsLoading(false);
    return true;
  };

  const summaries = [
    {
      adjustedColumns: columns,
      rows,
      addButtonText: 'Add PT Code',
      tab: 1,
      updateRow,
      createRow,
      deleteRow,
      hideEdit: Boolean(hideEdit)
    }
  ];

  return <CrudTableCard tabs={tabs} summaries={summaries} numColumns={1} isLoading={isLoading} />;
};

export default compose(
  connect(null, {
    showToast,
    postAuthPtCode,
    updateAuthPtCode,
    deleteAuthPtCode
  })
)(PtCodesCard);
